import React, { useState, useEffect } from 'react';
import { db } from '../../../firebase';
import { collection, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../../../hooks/useAuth';
import { RMS_PROVIDER_ROLES } from '../../../constants/userTypes';
import { RefreshIcon, UserAddIcon } from '@heroicons/react/outline';
import RmsProviderAccountsTable from '../../components/provider/RmsProviderAccountsTable';
import RmsProviderAccountSetupForm from '../../components/provider/RmsProviderAccountSetupForm';
import RmsTopNavBar from '../../components/provider/RmsTopNavBar';

/**
 * RmsStaffDashboard
 * -----------------
 * Purpose:
 *   Staff management page for RMS providers. Displays all provider accounts
 *   within the organisation and allows authorised users to invite new accounts.
 *
 * Features:
 *   - Table listing all RMS providers with sorting, pagination, and status
 *   - "Set Up New User" button to send an invite for a new provider account
 *   - Real-time subscription for live updates
 *   - Route access restricted to RMS Org. Admin (full) and RMS Support (read-only)
 *
 * Data:
 *   - Reads from the org-scoped subcollection:
 *     rms_organisations/{orgId}/rmsProviderUsers
 *   - This is a dedicated collection for RMS provider staff, separate from
 *     the ADHD Assessment Module's top-level `clinicians` collection.
 *
 * Authorization:
 *   - All RMS providers can view the staff list (for transparency)
 *   - Only RMS Org. Admin users can create new accounts
 */

export default function RmsStaffDashboard() {
  const { claims, user } = useAuth();
  const orgId = claims?.rmsOrgId;

  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSetupForm, setShowSetupForm] = useState(false);

  // Only RMS Org. Admin can create/manage staff. RMS Support reaches this page
  // read-only; RMS Clinician has no route access at all (A1 / TD-25). The backend
  // (rmsSetupNewProvider / rmsUpdateProviderUser) enforces the same gate.
  const canManageStaff = claims?.userType === RMS_PROVIDER_ROLES.ORG_ADMIN;

  // ─────────────────────────────────────────────────────────────────────────────
  // Data Loading
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Subscribe to the org-scoped rmsProviderUsers subcollection.
   * No client-side type filtering is needed because this collection only
   * contains RMS provider users (unlike the shared `clinicians` collection).
   */
  useEffect(() => {
    if (!orgId) return;

    const providerUsersRef = collection(
      db, 'rms_organisations', orgId, 'rmsProviderUsers'
    );

    const unsubscribe = onSnapshot(
      providerUsersRef,
      (snapshot) => {
        const allProviders = snapshot.docs.map((doc) => ({
          providerUserId: doc.id,
          ...doc.data(),
        }));

        setProviders(allProviders);
        setLoading(false);
      },
      (err) => {
        console.error('[RmsStaffDashboard] Error loading providers:', err);
        setError('Failed to load staff accounts');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [orgId]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Event Handlers
  // ─────────────────────────────────────────────────────────────────────────────

  const handleSetupSuccess = () => {
    // The real-time subscription will automatically update the table
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────────

  const breadcrumbs = [
    { label: 'Home', path: '/rms/provider/dashboard' },
    { label: 'Staff', path: null }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-secondary/15 flex flex-col">
      <RmsTopNavBar breadcrumbs={breadcrumbs} />

      <div className="flex-1 px-6 sm:px-8 lg:px-12 py-8">
        {/* Header */}
        <div className="mb-8 text-left">
          <h1 className="text-2xl font-bold text-gray-900">Staff Management</h1>
          <p className="text-sm text-gray-600 mt-1">
            View and manage provider accounts for your organisation
          </p>
        </div>

        {/* Actions bar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <span className="font-medium">{providers.length}</span> provider
              {providers.length !== 1 ? 's' : ''} in your organisation
            </div>
            {canManageStaff && (
              <button
                onClick={() => setShowSetupForm(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
              >
                <UserAddIcon className="w-4 h-4" />
                Set Up New User
              </button>
            )}
          </div>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <RefreshIcon className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-3" />
            <p className="text-gray-600">Loading staff accounts...</p>
          </div>
        )}

        {/* Error state */}
        {error && !loading && (
          <div className="bg-red-50 rounded-xl border border-red-200 p-6 text-center">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Provider table */}
        {!loading && !error && (
          <RmsProviderAccountsTable
            providers={providers}
            currentUserId={user?.uid}
            orgId={orgId}
            canManageStaff={canManageStaff}
          />
        )}

        {/* Setup form dialog */}
        <RmsProviderAccountSetupForm
          isOpen={showSetupForm}
          onClose={() => setShowSetupForm(false)}
          orgId={orgId}
          onSuccess={handleSetupSuccess}
        />
      </div>
    </div>
  );
}
